/**
 * Durable staging for verified-clean photos (M5 / S05-03).
 *
 * A `VerifiedPhoto` from S05-02 lives here — archive, display and thumbnail
 * bytes plus the delivery state — from the moment Brandon picks it until the
 * server has confirmed the archive, surviving app close, relaunch and process
 * death in between. IndexedDB `photos` namespace, separate from the Capture
 * draft (`records`) and the temporary audio (`audio`) stores.
 *
 * ONLY VERIFIED-CLEAN BYTES ARE STORABLE, AND THE TYPE SYSTEM ENFORCES IT:
 * `stagePhoto` accepts a `VerifiedPhoto`, whose brand is a module-private
 * symbol only `processPhoto` can issue — after the independent verifier
 * accepted all three variants. The unsanitized original is not part of that
 * type, so it structurally cannot be staged, and there is no other write path.
 *
 * OWNER SCOPING IS STRUCTURAL, THE SAME WAY THE DRAFT AND AUDIO STORES DO IT:
 * every key embeds the owner's user id, every read re-checks the stored
 * `ownerUserId` against the caller's, and a mismatch answers null rather than
 * data. Explicit sign-out purges by owner (D-027); session expiry calls
 * nothing here, so it purges nothing.
 *
 * THE STATE MODEL IS THE SMALLEST TRUTHFUL ONE — six stages, strictly ordered,
 * persisted after every transition so a restart resumes exactly where delivery
 * stopped:
 *
 *   staged → display-uploaded → thumb-uploaded → attached
 *          → archive-uploaded → confirmed
 *
 * `attached` carries the server row id, because confirmation needs it and a
 * restart must not have to guess it. `confirmed` records that the SERVER said
 * the archive arrived (D-028) — nothing local fakes it — and is immediately
 * followed by cleanup of the local bytes, which is the only removal path
 * besides sign-out.
 */

import type { LocalStorePort } from '../../platform/local-store';
import { selectPhotoStagingBackingStore } from '../../platform/local-store';
import type { VerifiedPhoto } from './photo-pipeline';

export const PHOTO_STAGING_SCHEMA_VERSION = 1;
export const PHOTO_STAGING_KEY_PREFIX = 'photo-staging:';

export function photoStagingOwnerPrefix(ownerUserId: string): string {
  return `${PHOTO_STAGING_KEY_PREFIX}${ownerUserId}:`;
}

export function photoStagingKey(ownerUserId: string, clientPhotoUuid: string): string {
  return `${photoStagingOwnerPrefix(ownerUserId)}${clientPhotoUuid}`;
}

/** Strictly ordered. A record never moves backwards. */
export const DELIVERY_STAGES = [
  'staged',
  'display-uploaded',
  'thumb-uploaded',
  'attached',
  'archive-uploaded',
  'confirmed',
] as const;

export type DeliveryStage = (typeof DELIVERY_STAGES)[number];

export const deliveryStageIndex = (stage: DeliveryStage): number => DELIVERY_STAGES.indexOf(stage);

interface StagedVariant {
  readonly bytes: Uint8Array;
  readonly byteSize: number;
  readonly width: number;
  readonly height: number;
  readonly mime: 'image/jpeg' | 'image/png';
}

export interface StagedPhoto {
  readonly schemaVersion: number;
  readonly ownerUserId: string;
  readonly captureId: string;
  /** Client-generated once at staging; every retry reuses it (D-025). */
  readonly clientPhotoUuid: string;
  readonly photoClass: 'before' | 'after';
  readonly archive: StagedVariant & { readonly sha256: string };
  readonly display: StagedVariant;
  readonly thumbnail: StagedVariant;
  /** The approved metadata subset, verbatim from the pipeline (S05-02). */
  readonly orientation: number | null;
  readonly capturedAtRaw: string | null;
  readonly utcOffsetMinutes: number | null;
  readonly sanitizerVersion: string;
  /** The SOURCE byte size at selection — what D-026's budget counts. */
  readonly sourceByteSize: number;
  readonly stage: DeliveryStage;
  /** The capture_photo row id, present from 'attached' onwards. */
  readonly serverPhotoId: string | null;
  readonly attempts: number;
  /** Last transport failure, for display. Never a coordinate, never a token. */
  readonly lastFailure: string | null;
  readonly stagedAtMs: number;
  readonly updatedAtMs: number;
}

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0;

const isBytes = (value: unknown): value is Uint8Array => value instanceof Uint8Array;

function parseVariant(value: unknown): StagedVariant | null {
  if (typeof value !== 'object' || value === null) return null;
  const v = value as Record<string, unknown>;
  if (!isBytes(v.bytes)) return null;
  if (typeof v.byteSize !== 'number' || v.byteSize !== v.bytes.length) return null;
  if (typeof v.width !== 'number' || typeof v.height !== 'number') return null;
  if (v.mime !== 'image/jpeg' && v.mime !== 'image/png') return null;
  return {
    bytes: v.bytes,
    byteSize: v.byteSize,
    width: v.width,
    height: v.height,
    mime: v.mime,
  };
}

/**
 * Parse a stored record, refusing anything malformed or owned by someone else.
 * A refusal answers null — recovery is simply not offered, never a crash and
 * never someone else's photo.
 */
export function parseStagedPhoto(value: unknown, ownerUserId: string): StagedPhoto | null {
  if (typeof value !== 'object' || value === null) return null;
  const record = value as Record<string, unknown>;
  if (record.schemaVersion !== PHOTO_STAGING_SCHEMA_VERSION) return null;
  // THE OWNER CHECK. The key is already owner-scoped; this re-checks the
  // record's own claim so a mis-filed record still cannot cross users.
  if (!isNonEmptyString(record.ownerUserId) || record.ownerUserId !== ownerUserId) return null;
  if (!isNonEmptyString(record.captureId) || !isNonEmptyString(record.clientPhotoUuid)) return null;
  if (record.photoClass !== 'before' && record.photoClass !== 'after') return null;
  if (!DELIVERY_STAGES.includes(record.stage as DeliveryStage)) return null;

  const archiveBase = parseVariant(record.archive);
  const display = parseVariant(record.display);
  const thumbnail = parseVariant(record.thumbnail);
  const sha = (record.archive as Record<string, unknown> | null | undefined)?.sha256;
  if (!archiveBase || !display || !thumbnail) return null;
  if (typeof sha !== 'string' || !/^[0-9a-f]{64}$/.test(sha)) return null;

  const stage = record.stage as DeliveryStage;
  const serverPhotoId = isNonEmptyString(record.serverPhotoId) ? record.serverPhotoId : null;
  // From 'attached' onwards the row id must exist — without it, confirmation
  // could not name the row, and a restart would have to guess. Refuse the
  // contradiction rather than resume wrongly.
  if (deliveryStageIndex(stage) >= deliveryStageIndex('attached') && serverPhotoId === null) {
    return null;
  }

  return {
    schemaVersion: PHOTO_STAGING_SCHEMA_VERSION,
    ownerUserId: record.ownerUserId,
    captureId: record.captureId,
    clientPhotoUuid: record.clientPhotoUuid,
    photoClass: record.photoClass,
    archive: { ...archiveBase, sha256: sha },
    display,
    thumbnail,
    orientation: typeof record.orientation === 'number' ? record.orientation : null,
    capturedAtRaw: isNonEmptyString(record.capturedAtRaw) ? record.capturedAtRaw : null,
    utcOffsetMinutes: typeof record.utcOffsetMinutes === 'number' ? record.utcOffsetMinutes : null,
    sanitizerVersion: isNonEmptyString(record.sanitizerVersion) ? record.sanitizerVersion : '',
    sourceByteSize: typeof record.sourceByteSize === 'number' ? record.sourceByteSize : 0,
    stage,
    serverPhotoId,
    attempts: typeof record.attempts === 'number' ? record.attempts : 0,
    lastFailure: isNonEmptyString(record.lastFailure) ? record.lastFailure : null,
    stagedAtMs: typeof record.stagedAtMs === 'number' ? record.stagedAtMs : 0,
    updatedAtMs: typeof record.updatedAtMs === 'number' ? record.updatedAtMs : 0,
  };
}

export interface PhotoStagingStore {
  /** Live, not a construction-time snapshot — the S-04-03 lesson. */
  readonly durable: boolean;
  /**
   * The owner's current PURGE GENERATION (R1 B-1). `purge()` bumps it
   * synchronously before deleting anything, and every write is guarded by it:
   * a delivery pass captures the epoch when it begins, and a persist whose
   * epoch is no longer current is refused — or undone, if the purge landed
   * while the write was in flight. This is what makes explicit sign-out FINAL:
   * an in-flight delivery cannot write the owner's photo bytes back.
   */
  purgeEpoch(ownerUserId: string): number;
  /**
   * Stage a verified-clean photo. The ONLY write path for new photos, and it
   * accepts ONLY the branded pipeline output. Returns the staged record after
   * READING IT BACK — storage fails by resolving, so the readback is the
   * durability proof, not the write's return. Epoch-guarded: a purge during
   * the write means no record, not a resurrected one.
   */
  stagePhoto(
    ownerUserId: string,
    captureId: string,
    photoClass: 'before' | 'after',
    photo: VerifiedPhoto,
    nowMs: number,
  ): Promise<StagedPhoto | null>;
  read(ownerUserId: string, clientPhotoUuid: string): Promise<StagedPhoto | null>;
  /** Every staged photo the owner has, oldest first — the queue's order. */
  list(ownerUserId: string): Promise<StagedPhoto[]>;
  /**
   * Persist a delivery-state transition. Same readback discipline. The caller
   * passes the epoch its pass captured at the start; if the owner purged since
   * (B-1), the write is refused — null, and nothing lands on disk.
   */
  update(record: StagedPhoto, expectedEpoch: number): Promise<StagedPhoto | null>;
  /** Remove one photo's local bytes — after confirmation, and only then. */
  removeStaged(ownerUserId: string, clientPhotoUuid: string): Promise<void>;
  /**
   * D-027: explicit sign-out. Removes every staged photo the owner has, and
   * bumps the owner's purge epoch FIRST — synchronously — so no delivery pass
   * that began before this call can persist anything afterwards (B-1).
   */
  purge(ownerUserId: string): Promise<void>;
}

export function createPhotoStagingStore(store: LocalStorePort): PhotoStagingStore {
  /**
   * Per-owner purge generations (R1 B-1). In-memory ON PURPOSE: the race this
   * closes — sign-out while a delivery pass is mid-flight — can only happen
   * inside one JS context, because a dead process has no in-flight passes and
   * a fresh one starts with no stale epoch to honour. Cross-context duplicate
   * delivery is the documented residual risk F-2, unchanged.
   */
  const epochs = new Map<string, number>();
  const epochOf = (ownerUserId: string): number => epochs.get(ownerUserId) ?? 0;

  const toStored = (record: StagedPhoto): Record<string, unknown> => ({
    // Field by field, not spread — a caller cannot smuggle extra data in.
    schemaVersion: record.schemaVersion,
    ownerUserId: record.ownerUserId,
    captureId: record.captureId,
    clientPhotoUuid: record.clientPhotoUuid,
    photoClass: record.photoClass,
    archive: {
      bytes: record.archive.bytes,
      byteSize: record.archive.byteSize,
      width: record.archive.width,
      height: record.archive.height,
      mime: record.archive.mime,
      sha256: record.archive.sha256,
    },
    display: { ...record.display },
    thumbnail: { ...record.thumbnail },
    orientation: record.orientation,
    capturedAtRaw: record.capturedAtRaw,
    utcOffsetMinutes: record.utcOffsetMinutes,
    sanitizerVersion: record.sanitizerVersion,
    sourceByteSize: record.sourceByteSize,
    stage: record.stage,
    serverPhotoId: record.serverPhotoId,
    attempts: record.attempts,
    lastFailure: record.lastFailure,
    stagedAtMs: record.stagedAtMs,
    updatedAtMs: record.updatedAtMs,
  });

  const writeAndReadBack = async (
    record: StagedPhoto,
    expectedEpoch: number,
  ): Promise<StagedPhoto | null> => {
    // B-1 GUARD, BEFORE THE WRITE: a pass that began before a sign-out purge
    // carries an older epoch, and its record must never touch disk again.
    if (expectedEpoch !== epochOf(record.ownerUserId)) return null;
    const key = photoStagingKey(record.ownerUserId, record.clientPhotoUuid);
    await store.set(key, toStored(record));
    // B-1 GUARD, AFTER THE WRITE: the purge may have run WHILE the set was in
    // flight — its key enumeration can miss a key that did not exist yet, so
    // deletion alone cannot be relied on. The bump is synchronous, so this
    // re-check sees it, and the write is UNDONE rather than left behind as a
    // resurrected record. This closes the read-then-write race completely:
    // either the purge sees the key and deletes it, or this undoes it.
    if (expectedEpoch !== epochOf(record.ownerUserId)) {
      await store.remove(key);
      return null;
    }
    // THE READBACK IS THE PROOF (D-010's discipline, applied to photos). The
    // storage layer fails by RESOLVING, so "set did not throw" means nothing.
    const persisted = parseStagedPhoto(await store.get(key), record.ownerUserId);
    if (persisted === null) return null;
    if (persisted.stage !== record.stage || persisted.clientPhotoUuid !== record.clientPhotoUuid) {
      return null;
    }
    return persisted;
  };

  return {
    get durable() {
      return store.durable;
    },

    async stagePhoto(ownerUserId, captureId, photoClass, photo, nowMs) {
      if (!isNonEmptyString(ownerUserId) || !isNonEmptyString(captureId)) return null;
      // Captured synchronously at entry: a purge landing during the write
      // below is seen by the guard, and the new record does not survive it.
      const epoch = epochOf(ownerUserId);
      const record: StagedPhoto = {
        schemaVersion: PHOTO_STAGING_SCHEMA_VERSION,
        ownerUserId,
        captureId,
        clientPhotoUuid: crypto.randomUUID(),
        photoClass,
        archive: {
          bytes: photo.archive.bytes,
          byteSize: photo.archive.byteSize,
          width: photo.archive.width,
          height: photo.archive.height,
          mime: photo.archive.mime,
          sha256: photo.archive.sha256,
        },
        display: {
          bytes: photo.display.bytes,
          byteSize: photo.display.byteSize,
          width: photo.display.width,
          height: photo.display.height,
          mime: photo.display.mime,
        },
        thumbnail: {
          bytes: photo.thumbnail.bytes,
          byteSize: photo.thumbnail.byteSize,
          width: photo.thumbnail.width,
          height: photo.thumbnail.height,
          mime: photo.thumbnail.mime,
        },
        orientation: photo.metadata.orientation,
        capturedAtRaw: photo.metadata.capturedAtRaw,
        utcOffsetMinutes: photo.metadata.utcOffsetMinutes,
        sanitizerVersion: photo.sanitizerVersion,
        sourceByteSize: photo.metadata.sourceByteSize,
        stage: 'staged',
        serverPhotoId: null,
        attempts: 0,
        lastFailure: null,
        stagedAtMs: nowMs,
        updatedAtMs: nowMs,
      };
      return await writeAndReadBack(record, epoch);
    },

    purgeEpoch(ownerUserId) {
      return epochOf(ownerUserId);
    },

    async read(ownerUserId, clientPhotoUuid) {
      if (!isNonEmptyString(ownerUserId) || !isNonEmptyString(clientPhotoUuid)) return null;
      return parseStagedPhoto(
        await store.get(photoStagingKey(ownerUserId, clientPhotoUuid)),
        ownerUserId,
      );
    },

    async list(ownerUserId) {
      if (!isNonEmptyString(ownerUserId) || !store.keys) return [];
      const keys = await store.keys(photoStagingOwnerPrefix(ownerUserId));
      const records: StagedPhoto[] = [];
      for (const key of keys) {
        const record = parseStagedPhoto(await store.get(key), ownerUserId);
        if (record) records.push(record);
      }
      // Oldest first — delivery order is arrival order.
      return records.sort((a, b) => a.stagedAtMs - b.stagedAtMs);
    },

    async update(record, expectedEpoch) {
      return await writeAndReadBack(record, expectedEpoch);
    },

    async removeStaged(ownerUserId, clientPhotoUuid) {
      if (!isNonEmptyString(ownerUserId) || !isNonEmptyString(clientPhotoUuid)) return;
      await store.remove(photoStagingKey(ownerUserId, clientPhotoUuid));
    },

    async purge(ownerUserId) {
      if (!isNonEmptyString(ownerUserId)) return;
      // THE BUMP COMES FIRST, SYNCHRONOUSLY (B-1). From this statement on,
      // every epoch-guarded write from a pass that began earlier is refused
      // or undone — the deletions below can then never be raced back.
      epochs.set(ownerUserId, epochOf(ownerUserId) + 1);
      if (!store.keys) return;
      // Owner-prefixed keys only: another user's staged photos are untouched.
      for (const key of await store.keys(photoStagingOwnerPrefix(ownerUserId))) {
        await store.remove(key);
      }
    },
  };
}

let applicationPhotoStagingStore: PhotoStagingStore | null = null;

export function getPhotoStagingStore(): PhotoStagingStore {
  applicationPhotoStagingStore ??= createPhotoStagingStore(selectPhotoStagingBackingStore());
  return applicationPhotoStagingStore;
}
